import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FarmData } from 'types/FarmData';
import { Tabs } from 'ui';
import FarmCardTabs from 'enums/farm-card-tabs';
import FarmDescription from './FarmDescription';
import FarmPlaces from './FarmPlaces';
import FarmDeliveries from './FarmDeliveries';
import FarmGallery from './FarmGallery';
import FarmContacts from './FarmContacts';
import FarmPartners from './FarmPartners';

interface FarmTabsProps {
  farm: FarmData;
  isEmarket: boolean;
}

interface FarmTabItem {
  key: string;
  label: string;
  children: JSX.Element;
}

const FarmTabs = ({ farm, isEmarket }: FarmTabsProps) => {
  const intl = useIntl();

  const infrastructures = useMemo(
    () =>
      (farm.farm_infrastructures ?? []).filter(
        (infra) => infra.status !== false
      ),
    [farm.farm_infrastructures]
  );
  const deliveries = useMemo(
    () =>
      (farm.deliveries ?? []).filter((delivery) => delivery.status !== false),
    [farm.deliveries]
  );
  const contacts = useMemo(
    () => (farm.contacts ?? []).filter((contact) => contact.status !== false),
    [farm.contacts]
  );

  const items = useMemo<FarmTabItem[]>(
    () =>
      [
        (!!farm.description || !!farm.services?.length) && {
          key: FarmCardTabs.DESCRIPTION,
          label: intl.formatMessage({ id: 'farms.description' }),
          children: (
            <FarmDescription
              description={farm.description}
              services={farm.services}
            />
          ),
        },
        !!infrastructures.length && {
          key: FarmCardTabs.WORKING_TIME,
          label: intl.formatMessage({ id: 'farms.working_time' }),
          children: (
            <FarmPlaces
              infrastructures={infrastructures}
              fallbackOpenHours={farm.working_time}
              farmName={farm.brand || farm.name}
              phone={farm.contact_phone}
              email={farm.contact_email}
            />
          ),
        },
        isEmarket &&
          !!deliveries.length && {
            key: FarmCardTabs.DELIVERY,
            label: intl.formatMessage({ id: 'farm_view.tab_deliveries' }),
            children: (
              <FarmDeliveries
                deliveries={deliveries}
                farmName={farm.brand || farm.name}
                phone={farm.contact_phone}
                email={farm.contact_email}
              />
            ),
          },
        (!!farm.images?.length || !!farm.youtube_link) && {
          key: FarmCardTabs.GALLERY,
          label: intl.formatMessage({ id: 'farm_view.tab_gallery' }),
          children: (
            <FarmGallery images={farm.images} youtubeLink={farm.youtube_link} />
          ),
        },
        !!contacts.length && {
          key: FarmCardTabs.CONTACTS,
          label: intl.formatMessage({ id: 'farms.contacts' }),
          children: <FarmContacts contacts={contacts} />,
        },
        !!farm.partner_farms?.length && {
          key: FarmCardTabs.PARTNERS,
          label: intl.formatMessage({ id: 'farms.partners' }),
          children: <FarmPartners partners={farm.partner_farms} />,
        },
      ].filter(Boolean) as FarmTabItem[],
    [farm, infrastructures, deliveries, contacts, isEmarket, intl]
  );

  const [activeKey, setActiveKey] = useState<string>();
  const selectedKey = items.some((item) => item.key === activeKey)
    ? activeKey
    : items[0]?.key;

  if (!items.length) return null;

  return (
    <div className={'farm-tabs'}>
      <Tabs
        type={'line'}
        items={items}
        activeKey={selectedKey}
        onChange={setActiveKey}
      />
    </div>
  );
};

export default FarmTabs;
